import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, ToggleButton, ToggleButtonGroup, Card, CardContent, TextField, MenuItem, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';

import { reportingCapacityAPI } from '../services/reportingCapacityApi';
import ReportingStackedBars from '../components/ReportingStackedBars';

const PERIODS = { last_month: 'Last month', last_year: 'Last 12 months', all_time: 'All time' };
const REGIONS = { 'us-west-2': 'USW2', 'eu-west-2': 'EUW2', 'eu-west-3': 'EUW3', 'ap-southeast-2': 'ASE2' };
const num = (n) => Number(n || 0).toLocaleString();
const gb = (n) => `${num(n)} GB`;
const month = (d) => new Date(d).toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
const errText = (err, fallback) => err.response?.data?.detail || fallback;

function Stat({ label, value, sub }) {
  return (
    <Card>
      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" component="div" noWrap>{value}</Typography>
        <Typography variant="caption" color="text.secondary">{sub || ' '}</Typography>
      </CardContent>
    </Card>
  );
}

export default function ReportingVolumes() {
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState('');
  const [dimension, setDimension] = useState('last_year');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    reportingCapacityAPI.volumesSummary().then((res) => setSummary(res.data)).catch((err) => setError(errText(err, 'Could not load volumes')));
  }, []);

  useEffect(() => {
    if (!selected) { setData(null); return undefined; }
    let live = true;
    setLoading(true); setError(''); setData(null);
    reportingCapacityAPI.volumes(selected, dimension)
      .then((res) => live && setData(res.data))
      .catch((err) => live && setError(errText(err, 'Could not load volumes')))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [selected, dimension]);

  const customers = [...(summary?.customers || [])].sort((a, b) => a.cust_name.localeCompare(b.cust_name));
  const snapshots = data?.snapshots || [];
  const regions = [...new Set(snapshots.flatMap((s) => s.by_region.map((r) => r.region)))];
  const latest = snapshots[snapshots.length - 1];
  const bars = snapshots.map((s) => ({ label: month(s.snapshot_date), values: Object.fromEntries(s.by_region.map((r) => [r.region, r.size_gb])) }));

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Volumes</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Allocated E: drive storage (EBS volumes) per customer, from the latest snapshot, with history by region.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
          <Stat label="Total allocated" value={gb(summary.total_gb)} sub={summary.snapshot_date} />
          <Stat label="E: volumes" value={num(summary.total_volumes)} />
          <Stat label="Customers with volumes" value={num(summary.customer_count)} />
          <Stat label="Largest customer" value={summary.customers[0]?.cust_name || '-'} sub={summary.customers[0] ? gb(summary.customers[0].total_gb) : ''} />
        </Box>
      )}
      {!summary && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
      {summary && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField select size="small" label="Customer" value={selected} onChange={(e) => setSelected(e.target.value)} sx={{ minWidth: 260 }}>
            <MenuItem value="">Select a customer...</MenuItem>
            {customers.map((c) => <MenuItem key={c.cust_code} value={c.cust_code}>{c.cust_name}</MenuItem>)}
          </TextField>
          <ToggleButtonGroup exclusive size="small" value={dimension} onChange={(e, v) => v && setDimension(v)} aria-label="Period">
            {Object.entries(PERIODS).map(([k, label]) => <ToggleButton key={k} value={k}>{label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Paper>
      )}
      {summary && !selected && (
        <Paper sx={{ minWidth: 0 }}>
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table size="small" stickyHeader aria-label="Allocated storage by customer">
              <TableHead><TableRow><TableCell>Customer</TableCell><TableCell>Location</TableCell><TableCell align="right">Volumes</TableCell><TableCell align="right">Allocated (GB)</TableCell></TableRow></TableHead>
              <TableBody>
                {summary.customers.map((c) => (
                  <TableRow key={c.cust_code} hover sx={{ cursor: 'pointer' }} onClick={() => setSelected(c.cust_code)}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{c.cust_name}</Typography><Typography variant="caption" color="text.secondary">{c.cust_code}</Typography></TableCell>
                    <TableCell>{c.location || '-'}</TableCell>
                    <TableCell align="right">{num(c.volume_count)}</TableCell>
                    <TableCell align="right">{num(c.total_gb)}</TableCell>
                  </TableRow>
                ))}
                {summary.customers.length === 0 && <TableRow><TableCell colSpan={4}>No volume data yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {data && !loading && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
            <Stat label="Current allocated" value={gb(data.latest_total_gb)} sub={latest?.snapshot_date} />
            <Stat label="Regions" value={regions.length} sub={regions.map((r) => REGIONS[r] || r).join(', ')} />
            <Stat label="Volumes (E: drive)" value={latest?.volumes.length || 0} />
          </Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>{data.customer.cust_name}: allocated storage over time (GB)</Typography>
            {bars.length ? <ReportingStackedBars bars={bars} keys={regions} labels={REGIONS} ariaLabel="Allocated storage over time by region" /> : <Typography color="text.secondary">No snapshots in this period.</Typography>}
          </Paper>
          {latest && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 2fr' }, gap: 2, alignItems: 'start' }}>
              <Paper sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>By region, {latest.snapshot_date}</Typography>
                <TableContainer>
                  <Table size="small" aria-label="By region">
                    <TableHead><TableRow><TableCell>Region</TableCell><TableCell align="right">Allocated (GB)</TableCell></TableRow></TableHead>
                    <TableBody>
                      {[...latest.by_region].sort((a, b) => b.size_gb - a.size_gb).map((r) => <TableRow key={r.region}><TableCell>{REGIONS[r.region] || r.region}</TableCell><TableCell align="right">{num(r.size_gb)}</TableCell></TableRow>)}
                      <TableRow><TableCell sx={{ fontWeight: 600 }}>Total</TableCell><TableCell align="right" sx={{ fontWeight: 600 }}>{num(latest.total_gb)}</TableCell></TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
              <Paper sx={{ minWidth: 0 }}>
                <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Individual volumes, {latest.snapshot_date}</Typography>
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader aria-label="Individual volumes">
                    <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Region</TableCell><TableCell align="right">Size (GB)</TableCell><TableCell>State</TableCell><TableCell>Volume ID</TableCell></TableRow></TableHead>
                    <TableBody>
                      {[...latest.volumes].sort((a, b) => b.size_gb - a.size_gb).map((v) => (
                        <TableRow key={`${v.volume_id}-${v.region}`} hover>
                          <TableCell>{v.volume_name || '-'}</TableCell>
                          <TableCell>{REGIONS[v.region] || v.region || '-'}</TableCell>
                          <TableCell align="right">{num(v.size_gb)}</TableCell>
                          <TableCell><Chip size="small" label={v.state || 'unknown'} color={v.state === 'in-use' ? 'success' : 'default'} variant="outlined" /></TableCell>
                          <TableCell><Typography variant="caption" color="text.secondary">{v.volume_id}</Typography></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
